import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useTenant } from './contexts/TenantContext';
import profileCompletionService from './services/profileCompletionService';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
// V2 Authentication Pages
import LoginV2 from './pages/auth/LoginV2';
import RegisterV2 from './pages/auth/RegisterV2';
import CompanyRegistration from './pages/auth/CompanyRegistration';
import OAuthCallback from './components/auth/OAuthCallback';
// Single Dashboard
import UnifiedDashboard from './pages/UnifiedDashboard';
import UserDashboard from './pages/UserDashboard';
// import PANVerification from './pages/PANVerification';
// import EquifaxReport from './pages/EquifaxReport';
// import BankStatementAnalysis from './pages/BankStatementAnalysis';
import VoterIdValidation from './pages/VoterIdValidation';
import CAMembershipVerification from './pages/CAMembershipVerification';
import ReportHistory from './pages/ReportHistory';
import CreditWallet from './pages/CreditWallet';
import TransactionHistory from './pages/TransactionHistory';
import Settings from './pages/Settings';
// Support Tickets — floating launcher + reporter's My Issues page (PR 7) + admin inbox (PR 8)
import RaiseIssueLauncher from './components/Support/RaiseIssueLauncher';
import ClaimRejectionNotifier from './components/notifications/ClaimRejectionNotifier';
import MyIssues from './pages/Support/MyIssues';
import AdminInbox from './pages/Support/AdminInbox';
import ProfileComplete from './pages/ProfileComplete';
import Profile from './pages/Profile';
import SharedLeads from './pages/SharedLeads';
import SharedReportViewer from './pages/SharedReportViewer';

// Marketplace
import { MarketplaceDashboard } from './pages/leadflow';
import MarketplaceBrowse from './pages/marketplace/Browse';
import MarketplaceListingDetail from './pages/marketplace/ListingDetail';
import MarketplaceMyListings from './pages/marketplace/MyListings';
import MarketplacePostLead from './pages/marketplace/PostLead';
import MarketplaceMyPurchases from './pages/marketplace/MyPurchases';
import MarketplaceAnalytics from './pages/marketplace/Analytics';
import MarketplaceMyInterests from './pages/marketplace/MyInterests';
import MarketplaceReceivedInterests from './pages/marketplace/ReceivedInterests';
import CommissionWallet from './pages/marketplace/CommissionWallet';
// Social Feed & Company Internal Feed
import { SocialFeed } from './pages/social';
import CompanyInternalFeed from './pages/social/CompanyInternalFeed';
import CompanyPagesListing from './pages/social/CompanyPagesListing';
// For Business — Company Pages Hub
import CompanyPagesHub from './pages/social/CompanyPagesHub';
import CompanyPageDetail from './pages/social/CompanyPageDetail';
// Collaboration
import { CollaborationsList, PendingInvitations, CollaborationDetails, PendingConversions } from './pages/collaboration';
// DSA Offers (Commission Marketplace)
import { BrowseOffers, PostOffer, MyOffers, ReceivedInvites, SentInvites, OfferDetail } from './pages/dsa-offers';
// Lead Broadcast (Corporate Lead Processing)
import AvailableOffers from './pages/leads/AvailableOffers';
import NotificationsPage from './pages/notifications/NotificationsPage';
import ReceivedClaims from './pages/leads/ReceivedClaims';
import CompanyLeadsDashboard from './pages/leads/CompanyLeadsDashboard';
import ReportViewer from './pages/ReportViewer';
import PlanCustomization from './pages/PlanCustomization';
import SubscriptionPlans from './pages/SubscriptionPlans';
import AdminRedirect from './pages/AdminRedirect';
import LandingPage from './pages/LandingPage';
import InterestingLanding from "./pages/website/InterestingLanding";
import Offering from "./pages/website/Offering";
import Pricing from "./pages/website/Pricing";
import QuickStart from "./pages/website/QuickStart";
import TestStyles from './pages/TestStyles';

import BillingHistory from './pages/BillingHistory';
import InvoiceManagement from './pages/admin/InvoiceManagement';
import TenantAdminCreditManagement from './pages/admin/TenantAdminCreditManagement';
import LendingNetworkAdmin from './pages/admin/LendingNetworkAdmin';
import DashboardLayout from './components/DashboardLayout';
// Static pages
import AboutUs from './components/pages/AboutUs';
import ContactUs from './components/pages/ContactUs';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import RefundPolicy from './components/pages/RefundPolicy';
import TermsAndConditions from './components/pages/TermsAndConditions';
// Dashboard legal pages
import DashboardPrivacyPolicy from './components/dashboard/legal/PrivacyPolicy';
import DashboardTermsAndConditions from './components/dashboard/legal/TermsAndConditions';
import DashboardRefundPolicy from './components/dashboard/legal/RefundPolicy';
import DashboardContactUs from './components/dashboard/support/ContactUs';
import DashboardAboutUs from './components/dashboard/company/AboutUs';
// Loan Management Components
import LoanTypes from './pages/LoanTypes';
import EligibilityRuleTypes from './pages/EligibilityRuleTypes';
import LoanApplications from './pages/LoanApplications';
import LoanProspects from './pages/LoanProspects';
import Lenders from './pages/Lenders';
import StandardDocuments from './components/loan/StandardDocuments.jsx';
import ApplicantType from './pages/ApplicantType';
import EmployementType from './pages/EmployementType';
// Subscription Management
import SubscriptionDashboard from './pages/subscription/SubscriptionDashboard';
import PlanSelection from './pages/subscription/PlanSelection';
// Dynamic report pages
import ReportGenerator from './pages/reports/ReportGenerator';
// Payment Gateway
import PaymentGateway from './pages/tenant/billing/PaymentGateway';
// Import report input components
import DoctorVerificationInputComponent from './components/reports/input/DoctorVerificationInputComponent';
import NurseVerificationInputComponent from './components/reports/input/NurseVerificationInputComponent';
import EquifaxInputComponent from './components/reports/input/EquifaxInputComponent';
import ExperianInputComponent from './components/reports/input/ExperianInputComponent';
import BankStatementInputComponent from './components/reports/input/BankStatementInputComponent';
import GlibBankStatementInputComponent from './components/reports/input/GlibBankStatementInputComponent';
import GlibGstReturnInputComponent from './components/reports/input/GlibGstReturnInputComponent';
import GlibGstReturnFetchInputComponent from './components/reports/input/GlibGstReturnFetchInputComponent';
import GlibItrFetchInputComponent from './components/reports/input/GlibItrFetchInputComponent';
import PANDetailsInputComponent from './components/reports/input/PANDetailsInputComponent';
import PAN2InputComponent from './components/reports/input/PAN2InputComponent';
import EmploymentHistoryInputComponent from './components/reports/input/EmploymentHistoryInputComponent';
import EHFUANInputComponent from './components/reports/input/EHFUANInputComponent';
import DrivingLicenceInputComponent from './components/reports/input/DrivingLicenceInputComponent';
import DrivingLicenceV2InputComponent from './components/reports/input/DrivingLicenceV2InputComponent';
import VoterIdInputComponent from './components/reports/input/VoterIdInputComponent';
import VoterID2InputComponent from './components/reports/input/VoterID2InputComponent';
import MobileBureauLookupInputComponent from './components/reports/input/MobileBureauLookupInputComponent';
import RCDetailsInputComponent from './components/reports/input/RCDetailsInputComponent';
import GSTINAdvancedInputComponent from './components/reports/input/GSTINAdvancedInputComponent';
// Surepass API Input Components
import TanVerifyInputComponent from './components/reports/input/TanVerifyInputComponent';
import TanAdvancedInputComponent from './components/reports/input/TanAdvancedInputComponent';
import TanTdsCheckInputComponent from './components/reports/input/TanTdsCheckInputComponent';
import TanCompanySearchInputComponent from './components/reports/input/TanCompanySearchInputComponent';
import TinVerificationInputComponent from './components/reports/input/TinVerificationInputComponent';
import IrdaiVerificationInputComponent from './components/reports/input/IrdaiVerificationInputComponent';
import DeathVerificationInputComponent from './components/reports/input/DeathVerificationInputComponent';
import PrefillReportInputComponent from './components/reports/input/PrefillReportInputComponent';
import PrefillByMobileInputComponent from './components/reports/input/PrefillByMobileInputComponent';
import EmploymentEmailCheckInputComponent from './components/reports/input/EmploymentEmailCheckInputComponent';
import SmsSendOtpInputComponent from './components/reports/input/SmsSendOtpInputComponent';
import SmsSubmitOtpInputComponent from './components/reports/input/SmsSubmitOtpInputComponent';
import DigilockerListDocumentsInputComponent from './components/reports/input/DigilockerListDocumentsInputComponent';
import DigilockerDownloadDocumentInputComponent from './components/reports/input/DigilockerDownloadDocumentInputComponent';
// New Surepass Components
import GstinInputComponent from './components/reports/input/GstinInputComponent';
import GstinByPanInputComponent from './components/reports/input/GstinByPanInputComponent';
import CompanyDetailsInputComponent from './components/reports/input/CompanyDetailsInputComponent';
import ReraV2InputComponent from './components/reports/input/ReraV2InputComponent';
import McaCreateOrderInputComponent from './components/reports/input/McaCreateOrderInputComponent';
import McaOrderListInputComponent from './components/reports/input/McaOrderListInputComponent';
import UdyogAadharInputComponent from './components/reports/input/UdyogAadharInputComponent';
import UdyamOtpSendInputComponent from './components/reports/input/UdyamOtpSendInputComponent';
import UdyamOtpSubmitInputComponent from './components/reports/input/UdyamOtpSubmitInputComponent';
import CibilReportInputComponent from './components/reports/input/CibilReportInputComponent';
import CibilReportPdfInputComponent from './components/reports/input/CibilReportPdfInputComponent';
import ItrCreateClientInputComponent from './components/reports/input/ItrCreateClientInputComponent';
import ItrCheckCredentialsInputComponent from './components/reports/input/ItrCheckCredentialsInputComponent';
import ItrDownloadProfileInputComponent from './components/reports/input/ItrDownloadProfileInputComponent';
import ItrDownloadItrInputComponent from './components/reports/input/ItrDownloadItrInputComponent';
import ItrDownload26asInputComponent from './components/reports/input/ItrDownload26asInputComponent';
import ItrDownloadItrVInputComponent from './components/reports/input/ItrDownloadItrVInputComponent';
import ItrForgetPasswordInputComponent from './components/reports/input/ItrForgetPasswordInputComponent';
import ItrSubmitOtpInputComponent from './components/reports/input/ItrSubmitOtpInputComponent';
import ItrDownloadAisV2InputComponent from './components/reports/input/ItrDownloadAisV2InputComponent';
import ItrGetAisInputComponent from './components/reports/input/ItrGetAisInputComponent';
import ItrGetProfileInputComponent from './components/reports/input/ItrGetProfileInputComponent';
import ItrGetItrDetailsInputComponent from './components/reports/input/ItrGetItrDetailsInputComponent';
import ItrGet26asDetailsInputComponent from './components/reports/input/ItrGet26asDetailsInputComponent';
import FaceMatchInputComponent from './components/reports/input/FaceMatchInputComponent';
import FaceLivenessInputComponent from './components/reports/input/FaceLivenessInputComponent';
import FaceLivenessV3InputComponent from './components/reports/input/FaceLivenessV3InputComponent';
import FaceLivenessV4InputComponent from './components/reports/input/FaceLivenessV4InputComponent';
import FaceBackgroundRemoverInputComponent from './components/reports/input/FaceBackgroundRemoverInputComponent';
import FaceExtractInputComponent from './components/reports/input/FaceExtractInputComponent';
import ElectricityOperatorListInputComponent from './components/reports/input/ElectricityOperatorListInputComponent';
import SeStateListInputComponent from './components/reports/input/SeStateListInputComponent';
import SeDetailsInputComponent from './components/reports/input/SeDetailsInputComponent';
import DigilockerInitializeInputComponent from './components/reports/input/DigilockerInitializeInputComponent';
import DigilockerStatusInputComponent from './components/reports/input/DigilockerStatusInputComponent';
import EsignInitializeInputComponent from './components/reports/input/EsignInitializeInputComponent';
import EsignInitializeNsdlStampInputComponent from './components/reports/input/EsignInitializeNsdlStampInputComponent';
import EsignInitializeSuresignInputComponent from './components/reports/input/EsignInitializeSuresignInputComponent';
import EsignInitializeHsmInputComponent from './components/reports/input/EsignInitializeHsmInputComponent';
import EsignGetUploadLinkInputComponent from './components/reports/input/EsignGetUploadLinkInputComponent';
import EsignSetBrandingInputComponent from './components/reports/input/EsignSetBrandingInputComponent';
// New Surepass Utility/eSign/MCA Components
import FuelPriceStateListInputComponent from './components/reports/input/FuelPriceStateListInputComponent';
import FuelPriceInputComponent from './components/reports/input/FuelPriceInputComponent';
import ImeiVerificationInputComponent from './components/reports/input/ImeiVerificationInputComponent';
import DomainVerificationInputComponent from './components/reports/input/DomainVerificationInputComponent';
import MobileAgeInputComponent from './components/reports/input/MobileAgeInputComponent';
import ElectricityBillInputComponent from './components/reports/input/ElectricityBillInputComponent';
import McaGetStatusInputComponent from './components/reports/input/McaGetStatusInputComponent';
import EsignUploadPdfViaLinkInputComponent from './components/reports/input/EsignUploadPdfViaLinkInputComponent';
import EsignGetSignedDocumentInputComponent from './components/reports/input/EsignGetSignedDocumentInputComponent';
import EsignGetAuditTrailInputComponent from './components/reports/input/EsignGetAuditTrailInputComponent';
import EsignGetStatusInputComponent from './components/reports/input/EsignGetStatusInputComponent';
import EsignGetReportInputComponent from './components/reports/input/EsignGetReportInputComponent';
// Unified guided flow components
import DigilockerFlowComponent from './components/reports/input/DigilockerFlowComponent';
import EsignFlowComponent from './components/reports/input/EsignFlowComponent';
import FaceVerificationFlowComponent from './components/reports/input/FaceVerificationFlowComponent';
import McaDocumentsFlowComponent from './components/reports/input/McaDocumentsFlowComponent';
import UdyamOtpFlowComponent from './components/reports/input/UdyamOtpFlowComponent';
import ItrFlowComponent from './components/reports/input/ItrFlowComponent';
import SeFlowComponent from './components/reports/input/SeFlowComponent';
import FuelPriceFlowComponent from './components/reports/input/FuelPriceFlowComponent';
import ElectricityFlowComponent from './components/reports/input/ElectricityFlowComponent';
import AadhaarVerificationOtpInputComponent from './components/reports/input/AadhaarVerificationOtpInputComponent';
import NotFound from './pages/NotFound';
import { Toast } from 'primereact/toast';
import ProtectedFeatureRoute from './components/ProtectedFeatureRoute';
import AdminUsers from './pages/AdminUsers';
import AdminNotificationSettings from './pages/admin/NotificationSettings';
import SuperAdminBorrowerReports from './pages/admin/SuperAdminBorrowerReports';
import UserManagementTest from './pages/UserManagementTest';
import CompanyUserManagement from './components/company/CompanyUserManagement';
import CompanyAgentManagement from './components/company/CompanyAgentManagement';
import CompanyRoleManagement from './components/company/CompanyRoleManagement';
import CompanyInvoiceManagement from './components/company/CompanyInvoiceManagement';
import CompanyCreditManagement from './components/company/CompanyCreditManagement';
import CompanyLendingNetworkAdmin from './pages/admin/CompanyLendingNetworkAdmin';
// Admin Management Components
import CompanySharingSettings from './components/admin/CompanySharingSettings';
// Storage Usage Pages
import MyStorageUsage from './pages/storage/MyStorageUsage';
import CompanyStorageUsage from './pages/storage/CompanyStorageUsage';
import TenantStorageUsage from './pages/storage/TenantStorageUsage';
// Tree Management Pages
import OrgChartPage from './pages/tree/OrgChartPage';
import MLMDashboardPage from './pages/tree/MLMDashboardPage';
// Social Profiling Page
import MyAgentProfile from './pages/agentProfile/MyAgentProfile';
import CreateProfile from './pages/agentProfile/CreateProfile';
import EditProfile from './pages/agentProfile/EditProfile';
import ViewAgentProfile from './pages/agentProfile/ViewAgentProfile';
// Networking Page
import MyNetwork from './pages/network/MyNetwork';
// Messaging Page
import Messaging from './pages/messaging/Messaging';

// PrimeReact CSS imports
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

// Self-hosted Inter font (replaces Google Fonts CDN — eliminates third-party SRI risk)
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

// Amplitude loaded lazily to avoid render-blocking scripts in <head>
const initAmplitude = () => import('./utils/amplitude').then(m => m.default());

// ── Code-split: heaviest screens in the app ──────────────────────────────────
// These were eagerly imported, so every route — including /app/login — pulled
// their entire dependency tree into the initial bundle. LeadWizard alone is
// ~457KB of source and drags in OffersComparison (~343KB) and
// LoanPortfolioStep (~302KB). Loading them on demand keeps first paint off the
// critical path; the <Suspense> around <Routes> supplies the fallback.
const LeadDetails = lazy(() => import('./pages/LeadDetails'));
const SharedLeadDetails = lazy(() => import('./pages/SharedLeadDetails'));
const SharedLeadWizardPage = lazy(() => import('./pages/SharedLeadWizardPage'));
const OfferCalculationLeadList = lazy(() => import('./pages/OfferCalculation/LeadList'));
const OfferCalculationLeadWizard = lazy(() => import('./pages/OfferCalculation/LeadWizard'));
const EligibilityCalculator = lazy(() => import('./pages/OfferCalculation/EligibilityCalculator'));
const ExistingLoans = lazy(() => import('./pages/OfferCalculation/ExistingLoans'));
const LoanPortfolio = lazy(() => import('./pages/OfferCalculation/LoanPortfolio'));
const LoanProducts = lazy(() => import('./pages/LoanProducts')); // ~152KB, admin-only

// Ultra-Dynamic Loan System Components (Lazy Loaded) - JavaScript files work
const DynamicLoanManager = lazy(() => import('./pages/DynamicLoanManager'));
const BankOfferComparison = lazy(() => import('./components/loan/BankOfferComparison'));
const LoanApplicationWorkflow = lazy(() => import('./pages/LoanApplicationWorkflow'));

// NOTE: TypeScript components with internal imports are temporarily disabled
// due to webpack not resolving .ts/.tsx files without extensions in import statements.
// The components exist but their internal imports like './steps/BasicDetailsStep' fail
// because webpack looks for .js files by default.
//
// TODO: Fix by either:
// 1. Adding explicit .tsx extensions to all internal imports in TypeScript files
// 2. Or configuring webpack to resolve .ts/.tsx extensions properly
//
// DISABLED COMPONENTS:
// - LoanProductList, LoanProductWizard, LoanProductEligibilityRules
// - FormBuilder, FormList
// - LoanTypeSelection, LoanProductSelection
// - DynamicFormRenderer
// - OfferComparison
// - DocumentUpload, AdditionalDetailsForm, ApplicationReview, ApplicationTracking
// - EligibilityRuleDesigner

// Admin Master Management - JavaScript file works
const MasterManagement = lazy(() => import('./pages/admin/MasterManagement'));

// User Loan Product Application - JavaScript file works
const LoanProductApplication = lazy(() => import('./components/user/LoanProductApplication'));

// Prototype — Social Section (lazy-loaded; tenant_admin only; will be removed after promotion)
const PrototypeSocialOverview = lazy(() => import('./pages/prototype/social/index'));
const PrototypeMyAgentProfile = lazy(() => import('./pages/prototype/social/MyAgentProfilePrototype'));
const PrototypeEditProfile = lazy(() => import('./pages/prototype/social/EditProfilePrototype'));
const PrototypeMyNetwork = lazy(() => import('./pages/prototype/social/MyNetworkPrototype'));
const PrototypeSocialFeed = lazy(() => import('./pages/prototype/social/SocialFeedPrototype'));
const PrototypeMessaging = lazy(() => import('./pages/prototype/social/MessagingPrototype'));
const PrototypeNotifications = lazy(() => import('./pages/prototype/social/NotificationsPrototype'));

// Prototype — Network section (tree redesign of /app/tree/mlm-network)
const PrototypeMLMNetwork = lazy(() => import('./pages/prototype/network/MLMNetworkPrototype'));

// Loading component for Suspense
const PageLoader = () => (
  <div className="flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
    <div className="text-center">
      <i className="pi pi-spin pi-spinner text-4xl text-primary mb-3"></i>
      <div className="text-lg text-600">Loading...</div>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const isAuthenticated = localStorage.getItem('authToken');

  // Initialize profile completion service when authenticated
  // Must call useEffect before any conditional returns
  useEffect(() => {
    if (isAuthenticated && !profileCompletionService.initialized) {
      console.log('Initializing profile completion service...');
      profileCompletionService.fetchProfileCompletion();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/app/login" replace />;
  }

  // RaiseIssueLauncher mounts here so the "Raise an Issue" button is
  // visible on EVERY authenticated page (per PR 7 spec). It's never
  // shown on login/landing/public pages because they don't go through
  // this wrapper.
  //
  // ClaimRejectionNotifier also mounts here — it fires once per
  // authenticated session to surface "your claim was auto-rejected"
  // toasts to displaced claimants (e.g. when a lead owner regenerated
  // offers in the wizard).
  return (
    <>
      {children}
      <RaiseIssueLauncher />
      <ClaimRejectionNotifier />
    </>
  );
};

// Opt in to the two v6 behaviours that become the default in React Router v7.
// Without this the router logs a "Future Flag Warning" for each one on every
// boot. Both are safe here: state updates simply get wrapped in
// React.startTransition, and the only splat routes in this app render
// <NotFound />, which does no relative navigation.
const ROUTER_FUTURE = { v7_startTransition: true, v7_relativeSplatPath: true };

function App() {
  const { loading, isAdminDomain, isTenantDomain, error } = useTenant();

  // Initialize Sanctum CSRF cookie so XSRF-TOKEN is present for all state-changing requests
  useEffect(() => {
    const protocol = window.location.protocol;
    const host = window.location.hostname;
    const port = window.location.port === '3000' ? ':8000' : (window.location.port ? `:${window.location.port}` : '');
    fetch(`${protocol}//${host}${port}/sanctum/csrf-cookie`, { credentials: 'include' }).catch(() => {});
  }, []);

  // Initialize Amplitude Analytics and Session Replay
  useEffect(() => {
    initAmplitude();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading tenant configuration...</div>;
  }

  if (error) {
    return <div className="flex items-center justify-center h-screen text-red-500">Error loading tenant: {error}</div>;
  }

  // Redirect admin subdomain to Nova admin panel
  if (isAdminDomain) {
    return <AdminRedirect />;
  }

  // Tenant-specific routes
  if (isTenantDomain) {
    return (
      <BrowserRouter future={ROUTER_FUTURE}>
        <Toast ref={(el) => window.toast = el} />
        {/* One boundary for every lazy route below, so heavy screens (the lead
            wizard, offers comparison, portfolio) can be code-split without each
            route needing its own Suspense wrapper. Routes that already wrap
            themselves keep working — nested boundaries are fine. */}
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Redirect root to /app */}
          <Route path="/" element={<Navigate to="/app" replace />} />
          
          {/* Public tenant routes */}
          <Route path="/app/login" element={<Login />} />
          <Route path="/app/register" element={<Register />} />
          <Route path="/app/forgot-password" element={<ForgotPassword />} />
          <Route path="/app/reset-password/:token" element={<ResetPassword />} />
          <Route path="/app/test-styles" element={<TestStyles />} />

           {/* Tenant Public Pages - Same as Central Domain */}
          <Route path="/app" element={<LandingPage />} />
          <Route path="/app/about" element={<AboutUs />} />
          <Route path="/app/contact" element={<ContactUs />} />
          <Route path="/app/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/app/refund-policy" element={<RefundPolicy />} />
          <Route path="/app/terms-and-conditions" element={<TermsAndConditions />} />
          
          {/* Static Pages - Available for all tenants */}
          {/* <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} /> */}
          
          {/* V2 Authentication Routes (Testing OAuth) */}
          <Route path="/app/auth/login-v2" element={<LoginV2 />} />
          <Route path="/app/auth/register-v2" element={<RegisterV2 />} />
          <Route path="/app/register/company" element={<CompanyRegistration />} />
          <Route path="/app/auth/oauth/callback" element={<OAuthCallback />} />
          
          {/* Payment Gateway (No Auth Required) */}
          <Route path="/app/payment-gateway/:companyName" element={<PaymentGateway />} />
        
          {/* Protected tenant routes */}
          {/* <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<UnifiedDashboard />} />
            <Route path="dashboard" element={<UnifiedDashboard />} /> */}
            {/* <Route path="pan-verification" element={<PANVerification />} /> */}
            {/* Legacy routes removed - now using the report routes below */}
          {/* Protected tenant routes */}
        <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route path="/app/dashboard" element={<UnifiedDashboard />} />
            <Route path="/app/my-dashboard" element={<UserDashboard />} />
            <Route path="/app/report-history" element={<ReportHistory />} />
            <Route path="/app/credit-wallet" element={<ProtectedFeatureRoute feature="credits"><CreditWallet /></ProtectedFeatureRoute>} />
            <Route path="/app/transaction-history" element={<TransactionHistory />} />

            <Route path="/app/billing-history" element={<ProtectedFeatureRoute feature="credits"><BillingHistory /></ProtectedFeatureRoute>} />
            <Route path="/app/admin/invoice-management" element={<ProtectedFeatureRoute feature="credits"><InvoiceManagement /></ProtectedFeatureRoute>} />
            <Route path="/app/admin/credit-management" element={<ProtectedFeatureRoute feature="credits"><TenantAdminCreditManagement /></ProtectedFeatureRoute>} />
            <Route path="/app/admin/company-sharing" element={<CompanySharingSettings />} />
            <Route path="/app/settings" element={<Settings />} />
            <Route path="/app/profile" element={<Profile />} />
            <Route path="/app/profile/complete" element={<ProfileComplete />} />
            {/* Support tickets — reporter's view (PR 7) + admin inbox (PR 8).
                AdminInbox enforces the role check internally; non-admins
                see a friendly "Admin only" message rather than redirect. */}
            <Route path="/app/support/my-issues" element={<MyIssues />} />
            <Route path="/app/support/inbox" element={<AdminInbox />} />
            <Route path="/app/reports/:reportId" element={<ReportViewer />} />
            <Route path="/app/admin/plan-customization" element={<PlanCustomization />} />
            <Route path="/app/admin/users" element={<AdminUsers />} />
            <Route path="/app/admin/notification-settings" element={<AdminNotificationSettings />} />
            <Route path="/app/admin/user-management" element={<UserManagementTest />} />
            <Route path="/app/admin/lending-network" element={<LendingNetworkAdmin />} />
            <Route path="/app/admin/borrower-reports" element={<SuperAdminBorrowerReports />} />

            {/* Company Admin Routes */}
            <Route path="/app/company/leads-dashboard" element={<CompanyLeadsDashboard />} />
            <Route path="/app/company/users" element={<CompanyUserManagement />} />
            <Route path="/app/company/agents" element={<CompanyAgentManagement />} />
            <Route path="/app/company/role-management" element={<CompanyRoleManagement />} />
            <Route path="/app/company/invoices" element={<CompanyInvoiceManagement />} />
            <Route path="/app/company/credits" element={<CompanyCreditManagement />} />
            <Route path="/app/company/lending-network" element={<CompanyLendingNetworkAdmin />} />

            {/* Storage Usage Routes */}
            <Route path="/app/storage/my" element={<MyStorageUsage />} />
            <Route path="/app/storage/company" element={<CompanyStorageUsage />} />
            <Route path="/app/admin/storage" element={<TenantStorageUsage />} />

            {/* Tree Management Routes */}
            <Route path="/app/tree/org-chart" element={<OrgChartPage />} />
            <Route path="/app/tree/mlm-network" element={<MLMDashboardPage />} />

            {/* Social Profiling Route */}
            <Route path="/app/my-agent-profile" element={<MyAgentProfile />} />
            <Route path="/app/create-profile" element={<CreateProfile />} />
            <Route path="/app/edit-profile" element={<EditProfile />} />
            <Route path="/app/profile/:profileId" element={<ViewAgentProfile />} />

            {/* Networking Route */}
            <Route path="/app/my-network" element={<MyNetwork />} />

            {/* Messaging Route */}
            <Route path="/app/messaging" element={<Messaging />} />

            {/* ── Social Section PROTOTYPE (tenant_admin demo only; removed after promotion) ── */}
            <Route path="/app/prototype/social" element={<Suspense fallback={<PageLoader />}><PrototypeSocialOverview /></Suspense>} />
            <Route path="/app/prototype/social/profile" element={<Suspense fallback={<PageLoader />}><PrototypeMyAgentProfile /></Suspense>} />
            <Route path="/app/prototype/social/profile/:userId" element={<Suspense fallback={<PageLoader />}><PrototypeMyAgentProfile /></Suspense>} />
            <Route path="/app/prototype/social/edit-profile" element={<Suspense fallback={<PageLoader />}><PrototypeEditProfile /></Suspense>} />
            <Route path="/app/prototype/social/network" element={<Suspense fallback={<PageLoader />}><PrototypeMyNetwork /></Suspense>} />
            <Route path="/app/prototype/social/feed" element={<Suspense fallback={<PageLoader />}><PrototypeSocialFeed /></Suspense>} />
            <Route path="/app/prototype/social/feed/:postId" element={<Suspense fallback={<PageLoader />}><PrototypeSocialFeed /></Suspense>} />
            <Route path="/app/prototype/social/messages" element={<Suspense fallback={<PageLoader />}><PrototypeMessaging /></Suspense>} />
            <Route path="/app/prototype/social/messages/:convId" element={<Suspense fallback={<PageLoader />}><PrototypeMessaging /></Suspense>} />
            <Route path="/app/prototype/social/notifications" element={<Suspense fallback={<PageLoader />}><PrototypeNotifications /></Suspense>} />
            <Route path="/app/prototype/network/mlm" element={<Suspense fallback={<PageLoader />}><PrototypeMLMNetwork /></Suspense>} />

            <Route path="/app/subscription/plans" element={<SubscriptionPlans />} />
            
            {/* Lead Management Routes - Using new 6-stage wizard flow */}
            <Route path="leads" element={<OfferCalculationLeadList />} />
            <Route path="leads/create" element={<OfferCalculationLeadWizard key="create" />} />
            <Route path="leads/edit/:leadId" element={<OfferCalculationLeadWizard key="edit" />} />
            <Route path="leads/view/:leadId" element={<OfferCalculationLeadWizard viewMode key="view" />} />
            <Route path="leads/:leadId" element={<LeadDetails />} />
            <Route path="leads/:leadId/existing-loans" element={<ExistingLoans />} />
            <Route path="leads/:leadId/portfolio" element={<LoanPortfolio />} />
            <Route path="leads/shared" element={<ProtectedFeatureRoute feature="shared-leads"><SharedLeads /></ProtectedFeatureRoute>} />
            <Route path="leads/shared/:shareId" element={<ProtectedFeatureRoute feature="shared-leads"><SharedLeadWizardPage /></ProtectedFeatureRoute>} />
            <Route path="leads/shared/:shareId/report/:reportType/:reportId" element={<ProtectedFeatureRoute feature="shared-leads"><SharedReportViewer /></ProtectedFeatureRoute>} />

            {/* Available Offers (Corporate Lead Broadcasting) */}
            <Route path="leads/available-offers" element={<AvailableOffers />} />
            <Route path="leads/received-claims" element={<ReceivedClaims />} />

            {/* Notifications inbox — claim auto-rejections, more types coming later */}
            <Route path="notifications" element={<NotificationsPage />} />

            {/* Eligibility Calculator - Available under Loan Calculators */}
            <Route path="eligibility-calculator" element={<EligibilityCalculator />} />

            {/* Marketplace Routes */}
            <Route path="marketplace-dashboard" element={<MarketplaceDashboard />} />
            <Route path="marketplace" element={<MarketplaceBrowse />} />
            <Route path="marketplace/listing/:id" element={<MarketplaceListingDetail />} />
            <Route path="marketplace/my-listings" element={<MarketplaceMyListings />} />
            <Route path="marketplace/post" element={<MarketplacePostLead />} />
            <Route path="marketplace/my-purchases" element={<MarketplaceMyPurchases />} />
            <Route path="marketplace/analytics" element={<MarketplaceAnalytics />} />
            <Route path="marketplace/my-interests" element={<MarketplaceMyInterests />} />
            <Route path="marketplace/received-interests" element={<MarketplaceReceivedInterests />} />

            {/* DSA Offers (Commission Marketplace) Routes */}
            <Route path="dsa-offers" element={<BrowseOffers />} />
            <Route path="dsa-offers/post" element={<PostOffer />} />
            <Route path="dsa-offers/edit/:id" element={<PostOffer />} />
            <Route path="dsa-offers/my-offers" element={<MyOffers />} />
            <Route path="dsa-offers/received-invites" element={<ReceivedInvites />} />
            <Route path="dsa-offers/sent-invites" element={<SentInvites />} />
            <Route path="dsa-offers/:id" element={<OfferDetail />} />

            {/* LeadFlow Wallet Route */}
            <Route path="leadflow/wallet" element={<CommissionWallet />} />

            {/* Social Feed Route */}
            <Route path="leadflow/feed" element={<SocialFeed />} />

            {/* Company Pages Directory & Individual Company View */}
            <Route path="company-pages" element={<CompanyPagesListing />} />
            <Route path="company-pages/:slug" element={<CompanyInternalFeed />} />
            <Route path="my-company" element={<CompanyInternalFeed />} />

            {/* For Business — Company Pages Hub */}
            <Route path="business/pages" element={<CompanyPagesHub />} />
            <Route path="business/pages/:slug" element={<CompanyPageDetail />} />


            {/* Collaboration Routes */}
            <Route path="collaborations" element={<CollaborationsList />} />
            <Route path="collaborations/invitations" element={<PendingInvitations />} />
            <Route path="collaborations/conversions" element={<PendingConversions />} />
            <Route path="collaborations/:id" element={<CollaborationDetails />} />

            {/* Legal & Support Pages */}
            <Route path="legal/privacy" element={<DashboardPrivacyPolicy />} />
            <Route path="legal/terms" element={<DashboardTermsAndConditions />} />
            <Route path="legal/refund" element={<DashboardRefundPolicy />} />
            <Route path="support/contact" element={<DashboardContactUs />} />
            <Route path="company/about" element={<DashboardAboutUs />} />
            
            {/* Dynamic Report Routes - service ID from URL - Protected */}
            <Route path="reports/equifax/:serviceId" element={<ProtectedFeatureRoute feature="equifax"><EquifaxInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/experian-report/:serviceId" element={<ProtectedFeatureRoute feature="experian"><ExperianInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/bank-statement-analysis/:serviceId" element={<ProtectedFeatureRoute feature="bank-statement"><BankStatementInputComponent /></ProtectedFeatureRoute>} />
            {/* GLIB (Autosift) BSA — same capability as the BoostMoney route above,
                so it reuses the `bank-statement` feature flag. */}
            <Route path="reports/glib-bank-statement-analysis/:serviceId" element={<ProtectedFeatureRoute feature="bank-statement"><GlibBankStatementInputComponent /></ProtectedFeatureRoute>} />
            {/* GLIB (Autosift) GST Return Analysis — GSTR-3B extraction. Uses the
                gstin-verification feature flag, matching the other GST services. */}
            <Route path="reports/glib-gst-return-analysis/:serviceId" element={<ProtectedFeatureRoute feature="gstin-verification"><GlibGstReturnInputComponent /></ProtectedFeatureRoute>} />
            {/* GST Return Fetch — borrower consent journey (no upload). Same
                capability family as the analysis route above, so it reuses the
                gstin-verification feature flag. */}
            <Route path="reports/glib-gst-return-fetch/:serviceId" element={<ProtectedFeatureRoute feature="gstin-verification"><GlibGstReturnFetchInputComponent /></ProtectedFeatureRoute>} />
            {/* ITR Fetch — borrower consent journey against the income-tax portal.
                Uses the `itr` flag, matching every existing ITR route; there is
                no "itr-verification" key. */}
            <Route path="reports/glib-itr-fetch/:serviceId" element={<ProtectedFeatureRoute feature="itr"><GlibItrFetchInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/pan-details/:serviceId" element={<ProtectedFeatureRoute feature="pan-verification"><PANDetailsInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/employment-history-from-mobile-comprehensive/:serviceId" element={<ProtectedFeatureRoute feature="employment-history"><EmploymentHistoryInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/uanNumberToEmploymentHistoryWithDoE/:serviceId" element={<ProtectedFeatureRoute feature="employment-history"><EHFUANInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/aadhaarVerificationWithOTP/:serviceId" element={<ProtectedFeatureRoute feature="aadhaar-verification"><AadhaarVerificationOtpInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/driving-licence-detailed/:serviceId" element={<ProtectedFeatureRoute feature="driving-licence"><DrivingLicenceInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/mobile-bureau-lookup/:serviceId" element={<ProtectedFeatureRoute feature="mobile-bureau"><MobileBureauLookupInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/voter-id-validation/:serviceId" element={<ProtectedFeatureRoute feature="voter-id"><VoterIdInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/ca-membership-verification/:serviceId" element={<ProtectedFeatureRoute feature="ca-membership"><CAMembershipVerification /></ProtectedFeatureRoute>} />
            <Route path="reports/doctor-registration-verification/:serviceId" element={<ProtectedFeatureRoute feature="doctor-verification"><DoctorVerificationInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/nurse-registration-verification/:serviceId" element={<ProtectedFeatureRoute feature="doctor-verification"><NurseVerificationInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/court-record-check/:serviceId" element={<ProtectedFeatureRoute feature="unknown-service"><div>Court Record Check Component</div></ProtectedFeatureRoute>} />
            <Route path="reports/pan-details-v2/:serviceId" element={<ProtectedFeatureRoute feature="pan-verification"><PAN2InputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/driving-licence-v2/:serviceId" element={<ProtectedFeatureRoute feature="driving-licence"><DrivingLicenceV2InputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/voter-id-details-v2/:serviceId" element={<ProtectedFeatureRoute feature="voter-id"><VoterID2InputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/rc-details/:serviceId" element={<ProtectedFeatureRoute feature="driving-licence"><RCDetailsInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/gstin-advanced/:serviceId" element={<ProtectedFeatureRoute feature="gstin-verification"><GSTINAdvancedInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass TAN Routes */}
            <Route path="reports/surepass-tan-verify/:serviceId" element={<ProtectedFeatureRoute feature="tan-verification"><TanVerifyInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-tan-advanced/:serviceId" element={<ProtectedFeatureRoute feature="tan-verification"><TanAdvancedInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-tan-tds-check/:serviceId" element={<ProtectedFeatureRoute feature="tan-verification"><TanTdsCheckInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-tan-company-search/:serviceId" element={<ProtectedFeatureRoute feature="tan-verification"><TanCompanySearchInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass TIN & IRDAI Routes */}
            <Route path="reports/surepass-tin-verification/:serviceId" element={<ProtectedFeatureRoute feature="tin-verification"><TinVerificationInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-irdai-verify/:serviceId" element={<ProtectedFeatureRoute feature="irdai-verification"><IrdaiVerificationInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass Death Certificate Route */}
            <Route path="reports/surepass-death-verification/:serviceId" element={<ProtectedFeatureRoute feature="death-verification"><DeathVerificationInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass Prefill Routes */}
            <Route path="reports/surepass-prefill-report/:serviceId" element={<ProtectedFeatureRoute feature="prefill-report"><PrefillReportInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-prefill-by-mobile/:serviceId" element={<ProtectedFeatureRoute feature="prefill-report"><PrefillByMobileInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass Employment Email Route */}
            <Route path="reports/surepass-employment-email-check/:serviceId" element={<ProtectedFeatureRoute feature="employment-email"><EmploymentEmailCheckInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass SMS Routes */}
            <Route path="reports/surepass-sms-send-otp/:serviceId" element={<ProtectedFeatureRoute feature="sms-otp"><SmsSendOtpInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-sms-submit-otp/:serviceId" element={<ProtectedFeatureRoute feature="sms-otp"><SmsSubmitOtpInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass Digilocker Routes */}
            <Route path="reports/surepass-digilocker-list-documents/:serviceId" element={<ProtectedFeatureRoute feature="digilocker"><DigilockerListDocumentsInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-digilocker-download-document/:serviceId" element={<ProtectedFeatureRoute feature="digilocker"><DigilockerDownloadDocumentInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass GSTIN Routes */}
            <Route path="reports/surepass-gstin/:serviceId" element={<ProtectedFeatureRoute feature="gstin"><GstinInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-gstin-by-pan/:serviceId" element={<ProtectedFeatureRoute feature="gstin"><GstinByPanInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass Company Routes */}
            <Route path="reports/surepass-company-details/:serviceId" element={<ProtectedFeatureRoute feature="company"><CompanyDetailsInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-rera-v2/:serviceId" element={<ProtectedFeatureRoute feature="rera"><ReraV2InputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-mca-create-order/:serviceId" element={<ProtectedFeatureRoute feature="mca"><McaCreateOrderInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-mca-order-list/:serviceId" element={<ProtectedFeatureRoute feature="mca"><McaOrderListInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass Udyam/MSME Routes */}
            <Route path="reports/surepass-udyog-aadhar/:serviceId" element={<ProtectedFeatureRoute feature="udyog"><UdyogAadharInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-udyam-otp-send/:serviceId" element={<ProtectedFeatureRoute feature="udyam"><UdyamOtpSendInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-udyam-otp-submit/:serviceId" element={<ProtectedFeatureRoute feature="udyam"><UdyamOtpSubmitInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass CIBIL Routes */}
            <Route path="reports/surepass-cibil-report/:serviceId" element={<ProtectedFeatureRoute feature="cibil"><CibilReportInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-cibil-report-pdf/:serviceId" element={<ProtectedFeatureRoute feature="cibil"><CibilReportPdfInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass ITR Routes */}
            <Route path="reports/surepass-itr-create-client/:serviceId" element={<ProtectedFeatureRoute feature="itr"><ItrCreateClientInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-itr-check-credentials/:serviceId" element={<ProtectedFeatureRoute feature="itr"><ItrCheckCredentialsInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-itr-download-profile/:serviceId" element={<ProtectedFeatureRoute feature="itr"><ItrDownloadProfileInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-itr-download-itr/:serviceId" element={<ProtectedFeatureRoute feature="itr"><ItrDownloadItrInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-itr-download-26as/:serviceId" element={<ProtectedFeatureRoute feature="itr"><ItrDownload26asInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-itr-download-itr-v/:serviceId" element={<ProtectedFeatureRoute feature="itr"><ItrDownloadItrVInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-itr-forget-password/:serviceId" element={<ProtectedFeatureRoute feature="itr"><ItrForgetPasswordInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-itr-submit-otp/:serviceId" element={<ProtectedFeatureRoute feature="itr"><ItrSubmitOtpInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-itr-download-ais-v2/:serviceId" element={<ProtectedFeatureRoute feature="itr"><ItrDownloadAisV2InputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-itr-get-ais/:serviceId" element={<ProtectedFeatureRoute feature="itr"><ItrGetAisInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-itr-get-profile/:serviceId" element={<ProtectedFeatureRoute feature="itr"><ItrGetProfileInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-itr-get-itr-details/:serviceId" element={<ProtectedFeatureRoute feature="itr"><ItrGetItrDetailsInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-itr-get-26as-details/:serviceId" element={<ProtectedFeatureRoute feature="itr"><ItrGet26asDetailsInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass Face Routes */}
            <Route path="reports/surepass-face-match/:serviceId" element={<ProtectedFeatureRoute feature="face"><FaceMatchInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-face-liveness/:serviceId" element={<ProtectedFeatureRoute feature="face"><FaceLivenessInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-face-liveness-v3/:serviceId" element={<ProtectedFeatureRoute feature="face"><FaceLivenessV3InputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-face-liveness-v4/:serviceId" element={<ProtectedFeatureRoute feature="face"><FaceLivenessV4InputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-face-background-remover/:serviceId" element={<ProtectedFeatureRoute feature="face"><FaceBackgroundRemoverInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-face-extract/:serviceId" element={<ProtectedFeatureRoute feature="face"><FaceExtractInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass Utility Routes */}
            <Route path="reports/surepass-electricity-operator-list/:serviceId" element={<ProtectedFeatureRoute feature="utility"><ElectricityOperatorListInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-se-state-list/:serviceId" element={<ProtectedFeatureRoute feature="utility"><SeStateListInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-se-details/:serviceId" element={<ProtectedFeatureRoute feature="utility"><SeDetailsInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass Digilocker Extended Routes */}
            <Route path="reports/surepass-digilocker-initialize/:serviceId" element={<ProtectedFeatureRoute feature="digilocker"><DigilockerInitializeInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-digilocker-status/:serviceId" element={<ProtectedFeatureRoute feature="digilocker"><DigilockerStatusInputComponent /></ProtectedFeatureRoute>} />
            {/* Unified guided flow routes */}
            <Route path="reports/surepass-digilocker-flow/:serviceId" element={<ProtectedFeatureRoute feature="digilocker"><DigilockerFlowComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-esign-flow/:serviceId" element={<ProtectedFeatureRoute feature="esign"><EsignFlowComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-face-flow/:serviceId" element={<ProtectedFeatureRoute feature="face"><FaceVerificationFlowComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-mca-flow/:createServiceId/:listServiceId/:statusServiceId" element={<ProtectedFeatureRoute feature="mca"><McaDocumentsFlowComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-udyam-otp-flow/:sendServiceId/:submitServiceId" element={<ProtectedFeatureRoute feature="udyam-otp"><UdyamOtpFlowComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-itr-flow/:createId" element={<ProtectedFeatureRoute feature="itr"><ItrFlowComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-se-flow/:stateListServiceId/:detailsServiceId" element={<ProtectedFeatureRoute feature="utility"><SeFlowComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-fuel-price-flow/:stateListServiceId/:fuelServiceId" element={<ProtectedFeatureRoute feature="utility"><FuelPriceFlowComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-electricity-flow/:operatorListServiceId/:billServiceId" element={<ProtectedFeatureRoute feature="utility"><ElectricityFlowComponent /></ProtectedFeatureRoute>} />
            {/* Surepass Esign Routes */}
            <Route path="reports/surepass-esign-initialize/:serviceId" element={<ProtectedFeatureRoute feature="esign"><EsignInitializeInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-esign-initialize-nsdl-stamp/:serviceId" element={<ProtectedFeatureRoute feature="esign"><EsignInitializeNsdlStampInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-esign-initialize-suresign/:serviceId" element={<ProtectedFeatureRoute feature="esign"><EsignInitializeSuresignInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-esign-initialize-hsm/:serviceId" element={<ProtectedFeatureRoute feature="esign"><EsignInitializeHsmInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-esign-get-upload-link/:serviceId" element={<ProtectedFeatureRoute feature="esign"><EsignGetUploadLinkInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-esign-set-branding/:serviceId" element={<ProtectedFeatureRoute feature="esign"><EsignSetBrandingInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-esign-upload-pdf-via-link/:serviceId" element={<ProtectedFeatureRoute feature="esign"><EsignUploadPdfViaLinkInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-esign-get-signed-document/:serviceId" element={<ProtectedFeatureRoute feature="esign"><EsignGetSignedDocumentInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-esign-get-audit-trail/:serviceId" element={<ProtectedFeatureRoute feature="esign"><EsignGetAuditTrailInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-esign-get-status/:serviceId" element={<ProtectedFeatureRoute feature="esign"><EsignGetStatusInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-esign-get-report/:serviceId" element={<ProtectedFeatureRoute feature="esign"><EsignGetReportInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass Utilities Routes */}
            <Route path="reports/surepass-fuel-price-state-list/:serviceId" element={<ProtectedFeatureRoute feature="utility"><FuelPriceStateListInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-fuel-price/:serviceId" element={<ProtectedFeatureRoute feature="utility"><FuelPriceInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-imei-verification/:serviceId" element={<ProtectedFeatureRoute feature="utility"><ImeiVerificationInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-domain-verification/:serviceId" element={<ProtectedFeatureRoute feature="utility"><DomainVerificationInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-mobile-age/:serviceId" element={<ProtectedFeatureRoute feature="utility"><MobileAgeInputComponent /></ProtectedFeatureRoute>} />
            <Route path="reports/surepass-electricity-bill/:serviceId" element={<ProtectedFeatureRoute feature="utility"><ElectricityBillInputComponent /></ProtectedFeatureRoute>} />
            {/* Surepass MCA Get Status Route */}
            <Route path="reports/surepass-mca-get-status/:serviceId" element={<ProtectedFeatureRoute feature="mca"><McaGetStatusInputComponent /></ProtectedFeatureRoute>} />

            {/* Loan Management Routes */}
            <Route path="loans/types" element={<LoanTypes />} />
            <Route path="loans/products" element={<LoanProducts />} />
            <Route path="loans/eligibility-rule-types" element={<EligibilityRuleTypes />} />
            <Route path="loans/applications" element={<LoanApplications />} />
            <Route path="loans/prospects" element={<LoanProspects />} />
            <Route path="loans/lenders" element={<Lenders />} />
            <Route path="loans/standard-documents" element={<StandardDocuments />} />
            <Route path="loans/applicant-types" element={<ApplicantType />} />
            <Route path="loans/employment-types" element={<EmployementType />} />

            {/* Admin Loan Product Management Routes - TEMPORARILY DISABLED (TypeScript import issues) */}
            <Route path="admin/loan-products" element={
              <div className="p-4"><div className="card"><h3>Loan Products</h3><p className="text-600">This feature is temporarily under maintenance due to build configuration. Please use the Loan Products page from the Loan Management section.</p></div></div>
            } />
            <Route path="admin/loan-products/new" element={
              <div className="p-4"><div className="card"><h3>New Loan Product</h3><p className="text-600">Feature under maintenance.</p></div></div>
            } />
            <Route path="admin/loan-products/:id/edit" element={
              <div className="p-4"><div className="card"><h3>Edit Loan Product</h3><p className="text-600">Feature under maintenance.</p></div></div>
            } />

            {/* Admin Form Builder Routes - TEMPORARILY DISABLED (TypeScript import issues) */}
            <Route path="admin/forms" element={
              <div className="p-4"><div className="card"><h3>Application Forms</h3><p className="text-600">Feature under maintenance.</p></div></div>
            } />
            <Route path="admin/forms/new" element={
              <div className="p-4"><div className="card"><h3>New Form</h3><p className="text-600">Feature under maintenance.</p></div></div>
            } />
            <Route path="admin/forms/:id/edit" element={
              <div className="p-4"><div className="card"><h3>Edit Form</h3><p className="text-600">Feature under maintenance.</p></div></div>
            } />

            {/* Admin Master Management Routes */}
            <Route path="admin/masters" element={
              <Suspense fallback={<PageLoader />}>
                <MasterManagement />
              </Suspense>
            } />

            {/* Admin Eligibility Rules Routes - TEMPORARILY DISABLED (TypeScript import issues) */}
            <Route path="admin/eligibility-rules/new" element={
              <div className="p-4"><div className="card"><h3>New Eligibility Rule</h3><p className="text-600">Feature under maintenance.</p></div></div>
            } />
            <Route path="admin/eligibility-rules/:id/edit" element={
              <div className="p-4"><div className="card"><h3>Edit Eligibility Rule</h3><p className="text-600">Feature under maintenance.</p></div></div>
            } />
            <Route path="admin/loan-products/:loanProductId/eligibility-rules/new" element={
              <div className="p-4"><div className="card"><h3>New Product Eligibility Rule</h3><p className="text-600">Feature under maintenance.</p></div></div>
            } />
            <Route path="admin/loan-products/:id/rules" element={
              <div className="p-4"><div className="card"><h3>Loan Product Eligibility Rules</h3><p className="text-600">Feature under maintenance.</p></div></div>
            } />

            {/* User Loan Selection Routes - TEMPORARILY DISABLED (TypeScript import issues) */}
            <Route path="user/loan-types" element={
              <div className="p-4"><div className="card"><h3>Select Loan Type</h3><p className="text-600">Feature under maintenance. Please use "Apply for Loan" instead.</p></div></div>
            } />
            <Route path="user/loan-products" element={
              <div className="p-4"><div className="card"><h3>Select Loan Product</h3><p className="text-600">Feature under maintenance. Please use "Apply for Loan" instead.</p></div></div>
            } />

            {/* User Loan Product Application */}
            <Route path="loans/apply/:productId" element={
              <Suspense fallback={<PageLoader />}>
                <LoanProductApplication />
              </Suspense>
            } />

            {/* User Loan Application Workflow v2.0 - TEMPORARILY DISABLED */}
            <Route path="loans/apply-workflow" element={
              <div className="p-4">
                <div className="card">
                  <h3>Loan Application Workflow</h3>
                  <p className="text-600">This feature is temporarily under maintenance. Please use "Apply for Loan" instead.</p>
                </div>
              </div>
            } />

            {/* User Application Form Routes - TEMPORARILY DISABLED (TypeScript import issues) */}
            <Route path="apply/:productId" element={
              <div className="p-4"><div className="card"><h3>Apply for Loan Product</h3><p className="text-600">Feature under maintenance. Please use "Apply for Loan" instead.</p></div></div>
            } />
            <Route path="apply/form/:formId" element={
              <div className="p-4"><div className="card"><h3>Loan Application Form</h3><p className="text-600">Feature under maintenance. Please use "Apply for Loan" instead.</p></div></div>
            } />

            {/* User Offer Comparison Routes - TEMPORARILY DISABLED (TypeScript import issues) */}
            <Route path="offers/compare/:applicationId" element={
              <div className="p-4"><div className="card"><h3>Compare Offers</h3><p className="text-600">Feature under maintenance.</p></div></div>
            } />
            <Route path="offers/compare" element={
              <div className="p-4"><div className="card"><h3>Compare Offers</h3><p className="text-600">Feature under maintenance.</p></div></div>
            } />

            {/* User Document Upload Routes - TEMPORARILY DISABLED (TypeScript import issues) */}
            <Route path="documents/upload/:applicationId" element={
              <div className="p-4"><div className="card"><h3>Upload Documents</h3><p className="text-600">Feature under maintenance.</p></div></div>
            } />
            <Route path="documents/additional-details/:applicationId" element={
              <div className="p-4"><div className="card"><h3>Additional Details</h3><p className="text-600">Feature under maintenance.</p></div></div>
            } />
            <Route path="application/review/:applicationId" element={
              <div className="p-4"><div className="card"><h3>Application Review</h3><p className="text-600">Feature under maintenance.</p></div></div>
            } />
            <Route path="application/tracking/:applicationId" element={
              <div className="p-4"><div className="card"><h3>Application Tracking</h3><p className="text-600">Feature under maintenance.</p></div></div>
            } />

            {/* Ultra-Dynamic Loan System Routes */}
            <Route path="loans/dynamic" element={
              <Suspense fallback={<PageLoader />}>
                <DynamicLoanManager />
              </Suspense>
            } />
            <Route path="loans/apply" element={
              <Suspense fallback={<PageLoader />}>
                <DynamicLoanManager />
              </Suspense>
            } />
            <Route path="loans/apply/:loanType" element={
              <Suspense fallback={<PageLoader />}>
                <DynamicLoanManager />
              </Suspense>
            } />
            <Route path="loans/offers/:applicationId" element={
              <Suspense fallback={<PageLoader />}>
                <BankOfferComparison />
              </Suspense>
            } />

            {/* Loan Application Workflow - Showcases DynamicFormBuilder & WorkflowTracker */}
            <Route path="loans/workflow" element={
              <Suspense fallback={<PageLoader />}>
                <LoanApplicationWorkflow />
              </Suspense>
            } />
            <Route path="loans/workflow/:applicationId" element={
              <Suspense fallback={<PageLoader />}>
                <LoanApplicationWorkflow />
              </Suspense>
            } />

            {/* Subscription Management Routes */}
            <Route path="/app/subscription" element={<SubscriptionDashboard />} />
            <Route path="/app/subscription/select-plan" element={<PlanSelection />} />

            {/* Dynamic Report Routes */}
            <Route path="reports/:reportType/:serviceId" element={<ReportGenerator />} />
          </Route>
          
          {/* Catch all - Shows NotFound without changing URL */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  // Main domain routes (landing page, marketing, etc.)
  return (
    <BrowserRouter future={ROUTER_FUTURE}>
      <Routes>
        <Route path="/" element={<Navigate to="/app" replace />} />
        {/* <Route path="/app" element={<LandingPage />} /> */}
        <Route path="/app" element={<InterestingLanding />} />
        <Route path="/app/offering" element={<Offering />} />
        <Route path="/app/pricing" element={<Pricing />} />
        <Route path="/app/quick-start" element={<QuickStart />} />
        <Route path="/app/about" element={<AboutUs />} />
        <Route path="/app/contact" element={<ContactUs />} />
        <Route path="/app/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/app/refund-policy" element={<RefundPolicy />} />
        <Route path="/app/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
